import { Gift } from "lucide-react";

import type { DisplayLoyaltyTier } from "@/api/orval/model";
import { useTranslation } from "@/core/i18n";

import type { HeldTierReward } from "./useHeldTierRewards";

type TierBenefitsProps = {
  readonly tier: DisplayLoyaltyTier;
  /** "onDark" on the celebration's backdrop; "onLight" on a card. */
  readonly tone: "onLight" | "onDark";
  /** The tier rewards the patient can use on this tier now; empty for a tier they don't hold. */
  readonly tierRewards: readonly HeldTierReward[];
};

/**
 * What holding a tier gives the client: its members-only text (the API sends it only on the tier
 * they hold) and its public description. Tier rewards join here.
 */
export const TierBenefits = ({ tier, tone, tierRewards }: TierBenefitsProps) => {
  const { t } = useTranslation();
  const { membersOnlyDescription, description } = tier;
  const primaryText = tone === "onDark" ? "text-white" : "text-primary";
  const secondaryText = tone === "onDark" ? "text-white/70" : "text-secondary";

  if (!membersOnlyDescription && !description && tierRewards.length === 0) {
    return <p className={`text-body2 ${secondaryText}`}>{t("loyalty.benefitsComingSoon")}</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {membersOnlyDescription && (
        <p className={`whitespace-pre-line text-body1 ${primaryText}`}>{membersOnlyDescription}</p>
      )}
      {tierRewards.length > 0 && (
        <ul aria-label={t("loyalty.tierRewardsLabel")} className="flex flex-col gap-1">
          {tierRewards.map(({ rewardId, title, usesLeft }) => (
            <li className={`flex items-center gap-2 text-body1 ${primaryText}`} key={rewardId}>
              <Gift aria-hidden className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1">{title}</span>
              {usesLeft && <span className={`text-body3 ${secondaryText}`}>{usesLeft}</span>}
            </li>
          ))}
        </ul>
      )}
      {description && <p className={`text-body2 ${secondaryText}`}>{description}</p>}
    </div>
  );
};
